import React from 'react';

import { CurrencyFormat } from '@ha/intl';
import { useIntl } from 'ha/i18n';
import { isBeforePromoDate } from 'ha/helpers/isBeforePromoDate';
import { useBookingLink } from 'ha/modules/BookingLink';
import { useFeatureFlags } from 'ha/modules/FeatureFlags';
import { Conversation } from '@ha/api/v2/types/Conversation';
import { RentalConditions } from '@ha/api/v2/types/RentalConditions';
import { isBaseFee } from 'ha/utils/isBaseFee';
import { useBillingSubscription } from 'ha/modules/BillingSubscription';

import { PricingStructure, FeeMode } from './types';
import { getPricingStructure } from './helpers';

interface PricingParams {
  listingId: number;
  rentalConditions: Pick<
    RentalConditions,
    'isLegacy' | 'startDate' | 'tenantFee' | 'baseTenantFee' | 'currency'
  >;
  conversation?: Pick<Conversation, 'bookingLink'>;
  tenantFeeRuleName: string;
  pricingStructureOverride?: PricingStructure;
}

interface PricingHook {
  pricingStructure: PricingStructure;
  feeMode: FeeMode;
  /** fee to use when special conditions apply */
  fee: string;
  /** regular fee when there is no promotions or fee removal  */
  baseFee: string;
}

export function usePricing({
  listingId,
  rentalConditions,
  conversation,
  tenantFeeRuleName,
  pricingStructureOverride,
}: PricingParams): PricingHook {
  const { T, formatCurrency } = useIntl();
  const { promotionalBookingFee } = useFeatureFlags();
  const { isEnabled: isBookingLinkEnabled } = useBookingLink(
    listingId,
    conversation,
  );
  const { billingSubscriptionEnabled } =
    useBillingSubscription(tenantFeeRuleName);

  const pricingStructure: PricingStructure = React.useMemo(() => {
    if (pricingStructureOverride) {
      return pricingStructureOverride;
    }

    return getPricingStructure({ rentalConditions });
  }, [pricingStructureOverride, rentalConditions]);

  const feeMode: FeeMode = React.useMemo(() => {
    const isPromotional =
      promotionalBookingFee === 'on' &&
      isBeforePromoDate(rentalConditions.startDate);
    if (isPromotional) {
      return FeeMode.promotional;
    }

    if (isBookingLinkEnabled || billingSubscriptionEnabled) {
      /**
       * Note that actual "total" price is calculated based on "fee" + "price" calculation
       * This pricing type will be used to hide information about fees, but won't affect calculations
       */
      return FeeMode.nofee;
    }

    return FeeMode.regular;
  }, [
    promotionalBookingFee,
    isBookingLinkEnabled,
    rentalConditions,
    billingSubscriptionEnabled,
  ]);

  const baseFee: string = React.useMemo(
    () =>
      formatCurrency(
        rentalConditions.tenantFee,
        rentalConditions.currency,
        rentalConditions.currency === 'EUR'
          ? CurrencyFormat.WITH_CURRENCY_SYMBOL_NO_FRACTION_NO_GROUPING
          : CurrencyFormat.WITH_CURRENCY_CODE_NO_FRACTION_NO_GROUPING,
      ),
    [rentalConditions, formatCurrency],
  );

  const fee: string = React.useMemo(() => {
    if (feeMode === FeeMode.nofee) {
      return '';
    }

    const isBase = isBaseFee(
      rentalConditions.tenantFee,
      rentalConditions.baseTenantFee,
    );
    if (isBase || feeMode === FeeMode.promotional) {
      return T('Free');
    }

    return baseFee;
  }, [feeMode, rentalConditions, baseFee, T]);

  return { pricingStructure, feeMode, fee, baseFee };
}
